import { inject, Pipe, type PipeTransform } from '@angular/core';
import type { ParamsOf, RouteName } from '@handyhand/i18n';
import { LocalizationService } from '@services/logic/localization.service';

export interface RoutePipeOptions<N extends RouteName> {
  params?: ParamsOf<N>;
  query?: Record<string, string | number | boolean>;
  absolute?: boolean;
}

/**
 * Resolves a named app route to a URL path. Thin wrapper around
 * `LocalizationService.route()` which owns the country resolution and
 * ensures routes always follow the hostname, never the user's profile
 * country.
 */
@Pipe({ name: 'route', standalone: true, pure: true })
export class RoutePipe implements PipeTransform {
  private readonly localizationService = inject(LocalizationService);

  transform<N extends RouteName>(
    name: N,
    options?: RoutePipeOptions<N>,
  ): string {
    const resolverOptions: {
      query?: Record<string, string | number | boolean>;
      absolute?: boolean;
    } = {};

    if (options?.query !== undefined) {
      resolverOptions.query = options.query;
    }

    if (options?.absolute !== undefined) {
      resolverOptions.absolute = options.absolute;
    }

    return this.localizationService.route(
      name,
      options?.params,
      resolverOptions,
    );
  }
}
